// Actor art (rebuild 2026-10-14): lifelike isometric sheets (tools/art/iso_chars.py: iso_<key>, 3/4 front and
// back facings mirrored to four directions), the Hollow Crawler, and WORN GEAR drawn on the figure (Matt:
// "Armor and items should be wearable and change the character's setup"):
//   body  the torso band recoloured in the item's colours and pattern (hi-vis stripes, plates, quilting)
//   feet  the boots recoloured
//   head  the item's icon on the head
//   weapon the item's icon in the hand, found from the frame's own pixels (it follows the swing)
// An actor is a Container: [base, body, feet, head, weapon]. Old 16x32 sheets still work as a fallback.
import { Assets, ColorMatrixFilter, Container, Filter, Rectangle, Sprite, Texture } from 'pixi.js';
import { Kind } from '../../shared/src/protocol';
import { ROSTER, CAST, CAST_SUB } from '../../shared/src/roster';
import type { IsoArt } from './isoart';
import GOODS from '../../data/goods.json';
import { OPTS } from './options';
import { ART_B, loadKtx } from './paintblock';

interface SheetMeta { frameW: number; frameH: number; cols?: number; anims: Record<string, number[]>; anchor?: [number, number]; iso?: boolean; stride_px?: number; res?: number;
  // studio 004 (route b): trim-packed sheets (frames[i] = [x, y, w, h, ox, oy]), the lying corpse frame, the bystanders' dim, the ring's size
  packed?: boolean; frames?: number[][]; corpse?: { w: number; h: number; anchor: [number, number]; rect: number[] }; idle_only?: boolean; dim?: number; shoulder_px?: number }
export interface Sheet { name: string; meta: SheetMeta; frames: Texture[]; alpha: Uint8ClampedArray | null; rgba: Uint8ClampedArray | null; W: number; cols: number; corpse?: Texture }
export interface Look { head: string; body: string; feet: string; weapon: string; offhand: string; hands: string }

const TURNED = ['iso_turned_commuter', 'iso_turned_pylon', 'iso_turned_beerleague', 'iso_turned_doorholder', 'hollow_crawler'];
const B_NPC = ['gull', 'tim', 'sean', 'shadow', 'survivor_0', 'survivor_1', 'survivor_2'];   // studio 004: painted bystanders (iso_<k>_bnpc)
const B_NPC_SET = new Set(B_NPC.map(k => `iso_${k}_bnpc`));
/** dev: the bystanders' idle sheets at res 3 (sheets_ktx.py --r3) instead of 4, for the crop read (?npcres=3) */
const NPC_R3 = new URLSearchParams(location.search).get('npcres') === '3';
const OLD_Z = ['zombie_commuter', 'zombie_pylon', 'zombie_beerleague', 'zombie_doorholder', 'hollow_crawler'];
const VIS: any = (GOODS as any).visuals ?? {};
const CASTERS = new Set(['steve', 'matt']);        // the mana friends: their attacks are casts (castTex, main.ts castPose)
/** studio 009h: the playable heroes on their painted sheets (iso_<key>_b, route b at 4x: idle, 8-frame walk, 3-frame swing, five
 *  drawn directions mirrored to eight, and the knocked-out corpse in meta.corpse). ?art=a keeps today's pixel sheets. The Gull
 *  keeps its own (no painted hero sheet) */
const HERO_B = new Set(['matt', 'props', 'sean', 'steve', 'todd', 'eljeffe']);
/** dev: ?herocheck=1 records every player frame drawn (tools/studio/hero_sheet_check.mjs reads window.__heroTrace) */
// cycle 011 round 2, the scale bible (Matt 2026-10-31: "vehicles and sprites that are poorly sized vs the player
// characters"): every figure drew 52 px, 2.0 m at the world's 26 px a vertical metre, so a 2.2 m door stood only 1.1x
// the hero (research B: 1.17-1.39x) and a sedan beside him was a quarter short. Figures draw at 0.88 (a door 1.24x the
// hero), cars at 1.1 (shared/src/iso.ts CAR_SCALE): a car is then ~2.15 heroes long, a true camera's 2.26.
export const FIG_SCALE = 0.88;
const HEROCHECK = new URLSearchParams(location.search).get('herocheck') === '1';

// a hit: the figure flashes near-white for a moment (one shared filter), then reads red while the hurt flag holds
// (Matt, 2026-10-15: "the attacked should be pushed slightly and flash briefly to indicate damage")
// cycle 009b (Matt: the brick edges "come and go as the character walks", "a nighttime/dusk thing"): a pixi filter renders
// at resolution 1 unless told otherwise, and route b's renderer is at `scale` (2-4 device px a logical px), so every filtered
// layer - the night grade over the whole street, a fading crown, a rim - was drawn at a half or a third of the screen's
// resolution and stretched back, sampled at a new phase each time the camera moved a device px. Every filter in the game
// renders at the renderer's own resolution (this runs before the first filter is made: FLASH below)
Filter.defaultOptions.resolution = 'inherit';
const FLASH = new ColorMatrixFilter();
FLASH.matrix = [0.25, 0, 0, 0, 0.82, 0, 0.25, 0, 0, 0.82, 0, 0, 0.25, 0, 0.82, 0, 0, 0, 1, 0];
export const FLASH_MS = 90;

/** the drawn attack frames over a swing's phase: hold the wind-up, snap to the strike, ease out on the follow-through
 *  (Diablo II's attack clips hold the raise and spend one or two frames on the hit) */
export function atkFrame(phase: number, n: number): number {
  if (n < 3) return Math.min(n - 1, Math.floor(phase * n));
  return phase < 0.4 ? 0 : phase < 0.62 ? 1 : 2;
}

/** what a 2D canvas can draw a texture's frame from: its own resource, or for a block-compressed painted sheet (studio 006)
 *  the alpha-only PNG beside it once it has loaded (null until then: the caller draws no outline this frame) */
export function drawable(t: Texture): CanvasImageSource | null {
  const s = t.source as any, r = s.resource;
  if (r && !Array.isArray(r) && !ArrayBuffer.isView(r)) return r as CanvasImageSource;
  const im = s.cpuImage as HTMLImageElement | undefined;
  return im && im.complete && im.naturalWidth ? im : null;
}

const ISO8: [string, boolean][] = [['dl', true], ['d', false], ['dl', false], ['l', false], ['ul', false], ['u', false], ['ul', true], ['l', true]];

export class ActorArt {
  sheets = new Map<string, Sheet>();
  dressCache = new Map<string, Texture>();
  geo = new Map<string, { top: number; bot: number; headX: number; handL: [number, number]; handR: [number, number] }>();
  constructor(public art: IsoArt | null = null) {}

  /** studio 006 (spec item 3): the painted sheets (route b) load when their kind is first asked for (sheetFor), as KTX2;
   *  until then the kind draws today's sheet. name -> 'loading' | 'ok' | 'failed' */
  lazy = new Map<string, string>();
  lazyBytes = 0; lazyFailed = 0;

  async load() {
    const names = [...TURNED, ...OLD_Z, 'iso_cellarman', 'iso_survivor_0', 'iso_survivor_1', 'iso_survivor_2',
      ...ROSTER.flatMap(r => [`iso_${r.key}`, `char_${r.key}`])];
    await Promise.all([...new Set(names)].map(n => this.loadSheet(n)));
  }

  /** a painted sheet by name, loading it (once) if it is not here yet */
  private painted(n: string): Sheet | undefined {
    const s = this.sheets.get(n); if (s) return s;
    if (!this.lazy.has(n)) { this.lazy.set(n, 'loading'); this.loadSheet(NPC_R3 && n.endsWith('_bnpc') ? n + '_r3' : n, n).then(ok => { this.lazy.set(n, ok ? 'ok' : 'failed'); if (!ok) this.lazyFailed++; }); }
    return undefined;
  }
  /** the sheet the last player figure drew from (the readout's art path: iso_<k>_b painted, iso_<k> the pixel sheet) */
  heroSheet = '';
  readout() { let n = 0; for (const v of this.lazy.values()) if (v === 'ok') n++; return `chars ${n}/${this.lazy.size} ${(this.lazyBytes / 1048576).toFixed(1)}MB fail ${this.lazyFailed}${this.heroSheet ? ' hero ' + this.heroSheet : ''}`; }

  /** studio 009h: a knocked-out figure lies as its sheet's own corpse frame (meta.corpse: head left, mirrored when it fell to the
   *  right, side > 0), not the standing frame tipped over - the turned since studio 004, the painted heroes since 009h. Call once
   *  the fall has landed; false (nothing changed) for a sheet with no corpse frame, which keeps the tipped fall */
  lieAsCorpse(c: Container, kind: number, sub: number, side: number): boolean {
    if (!ART_B || (kind !== Kind.Zombie && kind !== Kind.Player)) return false;
    const sh = this.sheetFor(kind, sub), C = sh?.meta.corpse;
    if (!sh?.corpse || !C) return false;
    const base = c.children[0] as Sprite, r = sh.meta.res ?? 1;
    base.texture = sh.corpse; base.anchor.set(C.anchor[0] / C.w, C.anchor[1] / C.h); base.scale.set((side > 0 ? -1 : 1) / r, 1 / r);
    c.rotation = 0; c.scale.y = Math.abs(c.scale.x);
    return true;
  }

  private async loadSheet(n: string, as = n): Promise<boolean> {
      try {
        const r = await fetch(`sprites/${n}.json`);
        if (!r.ok) return false;
        const meta: SheetMeta & { ktx2?: string } = await r.json();
        // a painted sheet ships as KTX2 (premultiplied, padded to whole blocks: the frames are unchanged)
        const tex = meta.ktx2 ? await loadKtx(`sprites/${meta.ktx2}`) : await Assets.load<Texture>(`sprites/${n}.png`);
        if (meta.ktx2 && (meta as any).alpha) { const im = new Image(); im.src = `sprites/${(meta as any).alpha}`; (tex.source as any).cpuImage = im; }   // (drawable(): the outline's CPU copy)
        if (meta.ktx2) { tex.source.alphaMode = 'premultiplied-alpha'; this.lazyBytes += tex.source.pixelWidth * tex.source.pixelHeight * (/rgba8/.test(String(tex.source.format)) ? 4 : 1); }
        (tex.source as any).res = meta.res ?? 1;   // (main.ts rimTex: your rim is one logical px wide whatever the sheet's res)
        if ((meta.res ?? 1) > 1) tex.source.scaleMode = 'linear';   // a painted sheet (route b) is sampled linear, never nearest
        const cols = meta.cols ?? Math.floor(tex.width / meta.frameW);
        const rows = Math.floor(tex.height / meta.frameH);
        const frames: Texture[] = [];
        // a trim-packed sheet (studio 004): each frame is its alpha box in the atlas, drawn at its offset inside the full cell
        // (orig = the cell, trim = the box), so anchors and frame sizes are the cell's; an empty cell is Texture.EMPTY
        const packed = (r: number[], W: number, H: number) => r[2] > 0 ? new Texture({ source: tex.source, frame: new Rectangle(r[0], r[1], r[2], r[3]),
          orig: new Rectangle(0, 0, W, H), trim: new Rectangle(r[4], r[5], r[2], r[3]) }) : Texture.EMPTY;
        if (meta.packed && meta.frames) for (const r of meta.frames) frames.push(packed(r, meta.frameW, meta.frameH));
        else for (let i = 0; i < cols * (meta.packed ? 0 : rows); i++)
          frames.push(new Texture({ source: tex.source, frame: new Rectangle((i % cols) * meta.frameW, Math.floor(i / cols) * meta.frameH, meta.frameW, meta.frameH) }));
        const corpse = meta.corpse?.rect ? packed(meta.corpse.rect, meta.corpse.w, meta.corpse.h) : undefined;
        let alpha: Uint8ClampedArray | null = null, rgba: Uint8ClampedArray | null = null;
        // the CPU copy feeds worn gear, the cast frame and geom(), all res 1 only: a painted sheet (res > 1) keeps none
        // (studio 004 G2: it was an unused 11.8 MB of heap for iso_matt_b)
        if (meta.iso && (meta.res ?? 1) === 1) {
          const img = new Image(); img.src = `sprites/${n}.png`; await img.decode();
          const cv = document.createElement('canvas'); cv.width = img.width; cv.height = img.height;
          const g = cv.getContext('2d', { willReadFrequently: true })!; g.drawImage(img, 0, 0);
          rgba = g.getImageData(0, 0, img.width, img.height).data;
          alpha = new Uint8ClampedArray(img.width * img.height); for (let i = 0; i < alpha.length; i++) alpha[i] = rgba[i * 4 + 3];
        }
        this.sheets.set(as, { name: as, meta, frames, alpha, rgba, W: tex.width, cols, corpse });
        return true;
      } catch { return false; /* missing art: fallback */ }
  }

  sheetFor(kind: number, sub: number): Sheet | undefined {
    // studio 008c (task #198): chapter 1's cast on their own painted sheets (sub CAST_SUB + i); the old art keeps today's
    const cast = sub >= CAST_SUB ? CAST[sub - CAST_SUB] : undefined;
    if (ART_B && cast && (kind === Kind.Npc || kind === Kind.Boss)) { const b = this.painted(`iso_${cast.key}_${cast.walks ? 'b' : 'bnpc'}`); if (b) return b; }
    if (kind === Kind.Boss) return this.sheets.get('iso_cellarman') ?? this.sheets.get('iso_turned_beerleague') ?? this.sheets.get('zombie_beerleague');
    if (kind === Kind.Zombie) return (ART_B ? this.painted(TURNED[sub % 5] + '_b') : undefined) ?? this.sheets.get(TURNED[sub % 5]) ?? this.sheets.get(OLD_Z[sub % 5]);
    if (ART_B && kind === Kind.Npc) {   // studio 004: a bystander's painted idle sheet, for Kind.Npc ONLY (a player or ally on that key keeps today's)
      const bn = sub < 200 ? `iso_${ROSTER[sub]?.key}_bnpc` : `iso_survivor_${sub % 3}_bnpc`;
      const b = B_NPC_SET.has(bn) ? this.painted(bn) : undefined; if (b) return b;
    }
    if ((kind === Kind.Player || kind === Kind.Npc) && sub < 200) {
      const k = ROSTER[sub]?.key;
      if (ART_B && k && HERO_B.has(k)) { const b = this.painted(`iso_${k}_b`); if (b) return b; }   // studio 003 Matt, 009h the other heroes: painted at 4x
      return (k && (this.sheets.get(`iso_${k}`) ?? this.sheets.get(`char_${k}`))) || undefined;
    }
    return this.sheets.get(`iso_survivor_${sub % 3}`);
  }

  /** a new actor container */
  make(): Container {
    const c = new Container();
    c.addChild(new Sprite(), new Sprite(), new Sprite(), new Sprite(), new Sprite());
    (c as any).actor = true;
    return c;
  }

  /** hurt: false, true (the red hold), or 'flash' (the first FLASH_MS of a hit).
   *  attacking: false, or the swing's PHASE 0..1 (wind-up, strike, recovery: atkFrame), never a looping clip. */
  apply(c: Container, kind: number, sub: number, dir: number, moving: boolean, attacking: boolean | number, hurt: boolean | 'flash', t: number, look?: Look, walked?: number) {
    const fl = hurt === 'flash' && !OPTS.reduceMotion;          // reduced motion: no white flash, the red hold only
    if (fl !== !!(c as any).flashing) { c.filters = fl ? [FLASH] : null; (c as any).flashing = fl; }
    const [base, body, feet, head, weap] = c.children as Sprite[];
    body.visible = feet.visible = head.visible = weap.visible = false;
    if (kind === Kind.Proj) return;
    const sh = this.sheetFor(kind, sub);
    if (!sh) { base.visible = false; return; }
    base.visible = true;
    const a = sh.meta.anims;
    let list: number[]; let flip = false;
    if (sh.meta.iso) {
      // dir: 8 world directions, 0 E 1 SE 2 S 3 SW 4 W 5 NW 6 N 7 NE. On screen (up = north-west) those are
      // lower-right, down, lower-left, left, upper-left, up, upper-right, right. The sheets draw the five that
      // face left or straight up/down; the other three are mirrors.
      const [v, f] = ISO8[dir & 7]; flip = f;
      const pick = (kind: string) => a[`${kind}_${v}`] ?? a[`${kind}_${v === 'u' || v === 'ul' ? 'ul' : 'dl'}`];
      list = attacking ? (pick('attack') ?? pick('idle')) : moving ? pick('walk') : (pick('idle') ?? pick('walk')?.slice(0, 1));
    } else {
      const D = ['right', 'down', 'down', 'down', 'left', 'up', 'up', 'up'][dir & 7] ?? 'down';
      list = (attacking && (a[`attack_${D}`] ?? a.attack)) || (moving ? a[`walk_${D}`] : (a[`idle_${D}`] ?? a.idle ?? a[`walk_${D}`]?.slice(0, 1))) || a[Object.keys(a)[0]];
    }
    const fps = moving ? 8 : 2;
    const phase = typeof attacking === 'number' ? attacking : attacking ? (t * 4) % 1 : -1;
    // a walk is paced by the distance covered on screen, one cycle per drawn stride (meta.stride_px, measured from
    // the drawn feet by tools/art/walk8.py), so a planted foot stays planted instead of sliding at a fixed rate
    const res = sh.meta.res ?? 1;   // a sheet drawn at res texels per logical px (route b: 4) is shown at 1/res; its stride is in its own px
    const stride = sh.meta.stride_px ? sh.meta.stride_px / res : undefined;
    const fi = (attacking !== false && list?.length) ? list[atkFrame(phase, list.length)]
      : (moving && stride && walked !== undefined && list?.length)
      ? list[Math.floor(walked / (stride * (c.scale.x || 1)) * list.length) % list.length]
      : list?.[Math.floor(t * fps) % list.length] ?? 0;
    base.texture = sh.frames[fi] ?? sh.frames[0];
    if (kind === Kind.Player) {
      this.heroSheet = sh.name;
      if (HEROCHECK) { (window as any).__heroSpr = c; const T = ((window as any).__heroTrace ??= []), tx = base.texture; T.push({ u: c.uid, s: sh.name, fi, dir, mv: moving, at: attacking !== false, fl: flip, e: tx === Texture.EMPTY, w: tx.frame.width, h: tx.frame.height }); if (T.length > 4000) T.splice(0, 1000); }
    }
    // a caster's attack is a CAST (Diablo review R1: Steve and Matt cast in their punch): the idle frame with the
    // hanging arms taken off and both forearms raised over the head, drawn in code (castTex); the body pose is
    // main.ts castPose. The weapon stays down.
    const casting = attacking !== false && sh.meta.iso && res === 1 && CASTERS.has(sh.name.slice(4)) && (kind === Kind.Player || kind === Kind.Ally);   // (route b: the drawn 3-frame swing, no code-drawn cast)
    if (casting) { const idle = (sh.meta.iso ? a[`idle_${ISO8[dir & 7][0]}`] : null)?.[0]; const ct = idle !== undefined ? this.castTex(sh, idle) : null; if (ct) base.texture = ct; }
    (c as any).casting = !!casting;
    const anc = sh.meta.anchor ?? [sh.meta.frameW / 2, sh.meta.frameH - 1];
    for (const s of [base, body, feet]) { s.anchor.set(anc[0] / sh.meta.frameW, anc[1] / sh.meta.frameH); s.scale.x = (flip ? -1 : 1) / res; s.scale.y = 1 / res; }
    // whole numbers only (STYLE H6.1): the Cellarman's sheet is cut at his 1.35x display size (tools/art/iso_chars.py
    // BIG), so he draws 1:1; a boss on the fallback sheet and the legacy big turned draw at 2x
    const big = kind === Kind.Boss ? (sh.name === 'iso_cellarman' || (sh.meta.res ?? 1) > 1 ? 1 : 2) : (sub % 5 === 4 && kind === Kind.Zombie && !sh.meta.iso ? 2 : 1);
    c.scale.set(big * FIG_SCALE);
    // idle breathing: a 1-frame idle reads as a statue (the turned sway instead, below, in main.ts)
    if (!moving && attacking === false) c.scale.y = big * FIG_SCALE * (1 + 0.014 * Math.sin(t * 2.4 + sub));
    base.tint = hurt && !fl ? 0xff6a6a : 0xffffff;
    if (!look || !sh.meta.iso || !sh.rgba || res !== 1) return;   // (route b: worn gear and the held weapon are cut from the proof)
    const g = this.geom(sh, fi);
    if (!g) return;
    const fw = sh.meta.frameW, fh = sh.meta.frameH;
    const X = (x: number) => (flip ? (fw - 1 - x) : x) - anc[0], Y = (y: number) => y - anc[1];
    // body and boots: recoloured layers cut from this frame
    const bv = look.body && VIS.body?.[look.body];
    if (bv) { body.texture = this.dress(sh, fi, 'b:' + look.body, bv.c, bv.p, bv.s, g.top + (g.bot - g.top) * 0.17, g.top + (g.bot - g.top) * 0.56, look.body.includes('vest')); body.visible = true; }
    const fv = look.feet && VIS.feet?.[look.feet];
    if (fv) { feet.texture = this.dress(sh, fi, 'f:' + look.feet, fv, 'plain', '', g.bot - (g.bot - g.top) * 0.07, g.bot + 1, false); feet.visible = true; }
    // headgear: the item's icon on the head
    const hi = look.head && this.art?.icon(look.head);
    if (hi) { head.texture = hi; head.anchor.set(0.5, 0.75); const s = 13 / Math.max(hi.width, 1); head.scale.set(flip ? -s : s, s); head.x = X(g.headX); head.y = Y(g.top + 4); head.visible = true; }
    // weapon: the item's icon in the leading hand, angled like it is held
    const wi = !casting && look.weapon && this.art?.icon(look.weapon);
    if (wi) {
      const [hx, hy] = g.handL;
      weap.texture = wi; weap.anchor.set(0.8, 0.8);
      const s = 18 / Math.max(wi.width, wi.height, 1); weap.scale.set(flip ? -s : s, s);
      weap.rotation = (flip ? 1 : -1) * (attacking === false ? 0.35 : phase < 0.4 ? 0.9 : -0.9);   // raised on the wind-up, through on the strike
      weap.x = X(hx); weap.y = Y(hy); weap.visible = true;
    }
  }

  /** the cast frame: one idle frame with its hanging arms removed and both forearms raised above the head, in the
   *  figure's own sleeve and skin colours with the sheet's dark edge. Cached per sheet frame. No new art. */
  castCache = new Map<string, Texture | null>();
  castTex(sh: Sheet, fi: number): Texture | null {
    const key = sh.name + ':' + fi;
    if (this.castCache.has(key)) return this.castCache.get(key)!;
    const g0 = this.geom(sh, fi); if (!g0 || !sh.rgba) { this.castCache.set(key, null); return null; }
    const fw = sh.meta.frameW, fh = sh.meta.frameH, ox = (fi % sh.cols) * fw, oy = Math.floor(fi / sh.cols) * fh;
    const d = new Uint8ClampedArray(fw * fh * 4);
    for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) { const si = ((oy + y) * sh.W + ox + x) * 4, o = (y * fw + x) * 4; for (let k = 0; k < 4; k++) d[o + k] = sh.rgba[si + k]; }
    const A = (x: number, y: number) => x >= 0 && y >= 0 && x < fw && y < fh && d[(y * fw + x) * 4 + 3] > 0;
    const px = (x: number, y: number): [number, number, number] => { const o = (y * fw + x) * 4; return [d[o], d[o + 1], d[o + 2]]; };
    const skin = (c: [number, number, number]) => c[0] > 90 && c[0] > c[1] + 12 && c[1] > c[2] && c[0] - c[2] > 35;
    const { top, bot, headX } = g0, H = bot - top;
    // the torso's half-width: the narrowest run across the chest rows is the trunk without the arms
    let core = fw;
    for (let y = Math.floor(top + H * 0.3); y < top + H * 0.42; y++) {
      let a = -1, b = -1; for (let x = Math.round(headX); x >= 0 && A(x, y); x--) a = x; for (let x = Math.round(headX); x < fw && A(x, y); x++) b = x;
      if (a >= 0) core = Math.min(core, Math.max(headX - a, b - headX));
    }
    core = Math.max(4, Math.min(core, fw * 0.22));
    // colours: the sleeve at the shoulder, the skin of the hanging arm
    let sleeve: [number, number, number] = [40, 40, 44], sk: [number, number, number] = [200, 150, 120];
    for (let y = Math.floor(top + H * 0.24); y < top + H * 0.3; y++) { const x = Math.round(headX + core - 1); if (A(x, y)) { sleeve = px(x, y); break; } }
    { let n = 0; const acc = [0, 0, 0];                     // the figure's own skin: the mean of its skin-toned pixels
      for (let y = top; y <= bot; y++) for (let x = 0; x < fw; x++) if (A(x, y)) { const c = px(x, y); if (skin(c)) { acc[0] += c[0]; acc[1] += c[1]; acc[2] += c[2]; n++; } }
      if (n > 4) sk = [acc[0] / n, acc[1] / n, acc[2] / n]; }
    // take the hanging arms off: outside the trunk in the arm band, and any skin below the chest
    for (let y = Math.floor(top + H * 0.22); y < top + H * 0.7; y++) for (let x = 0; x < fw; x++) {
      if (!A(x, y)) continue;
      if ((y < top + H * 0.64 && Math.abs(x - headX) > core + 0.5) || (y > top + H * 0.42 && skin(px(x, y)))) d[(y * fw + x) * 4 + 3] = 0;
    }
    // the hands that sit over the trunk (from behind and side-on the arms are inside its silhouette, R1-021): every
    // skin-toned pixel from below the chin to the hips, in a wider skin test that takes the shaded backs of hands
    const skin2 = (c: [number, number, number]) => c[0] > 60 && c[0] >= c[1] + 8 && c[1] >= c[2] - 4 && c[0] - c[2] > 22;
    for (let y = Math.floor(top + H * 0.28); y < top + H * 0.75; y++) for (let x = 0; x < fw; x++) {
      const si = ((oy + y) * sh.W + ox + x) * 4;
      if (A(x, y) && skin2([sh.rgba[si], sh.rgba[si + 1], sh.rgba[si + 2]])) d[(y * fw + x) * 4 + 3] = 0;
    }
    // and what is left of them: specks cut off from the body (a 4-connected group under 12 px) go
    { const seen = new Uint8Array(fw * fh);
      for (let i0 = 0; i0 < fw * fh; i0++) {
        if (!d[i0 * 4 + 3] || seen[i0]) continue;
        const st = [i0], comp: number[] = []; seen[i0] = 1;
        while (st.length) { const i = st.pop()!; comp.push(i); const x = i % fw, y = (i / fw) | 0;
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const xx = x + dx, yy = y + dy, j = yy * fw + xx;
            if (xx >= 0 && yy >= 0 && xx < fw && yy < fh && d[j * 4 + 3] && !seen[j]) { seen[j] = 1; st.push(j); } } }
        if (comp.length < 12) for (const i of comp) d[i * 4 + 3] = 0;
      } }
    // side-on, the cut-off hand leaves a stub sticking out of the hip and a notch in it (R1-021 round 5): a row in the
    // waist band whose edge sticks more than 2 px past the median edge of the rows around it is trimmed back to it, then
    // a clear run of up to 6 px inside the trunk with the body on both sides is filled with the trunk's own colour in
    // that row, so the outline has no inner edge to ring
    { const y0 = Math.floor(top + H * 0.3), y1 = Math.ceil(top + H * 0.62), Lx = new Map<number, number>(), Rx = new Map<number, number>();
      for (let y = y0 - 4; y < y1 + 5; y++) { if (y < 0 || y >= fh) continue; let l = -1, r = -1; for (let x = 0; x < fw; x++) if (d[(y * fw + x) * 4 + 3]) { if (l < 0) l = x; r = x; } if (l >= 0) { Lx.set(y, l); Rx.set(y, r); } }
      const med = (M: Map<number, number>, y: number) => { const v: number[] = []; for (let k = y - 4; k <= y + 4; k++) if (k !== y && M.has(k)) v.push(M.get(k)!); v.sort((a, b) => a - b); return v.length ? v[v.length >> 1] : -1; };
      const cut: [number, number, number][] = [];
      for (let y = y0; y < y1; y++) { if (!Lx.has(y)) continue; const ml = med(Lx, y), mr = med(Rx, y);
        if (ml >= 0 && Lx.get(y)! < ml - 2) cut.push([y, 0, ml]); if (mr >= 0 && Rx.get(y)! > mr + 2) cut.push([y, mr + 1, fw]); }
      for (const [y, a, b] of cut) for (let x = a; x < b; x++) d[(y * fw + x) * 4 + 3] = 0; }
    for (let y = Math.floor(top + H * 0.28); y < top + H * 0.6; y++) {   // above the crotch, so the gap between the legs stays
      let mid = -1, md = 1e9;
      for (let x = 0; x < fw; x++) if (Math.abs(x - headX) <= core + 1 && d[(y * fw + x) * 4 + 3] && Math.abs(x - headX) < md) { md = Math.abs(x - headX); mid = x; }
      if (mid < 0) continue;
      for (let x = 1; x < fw - 1; x++) {
        if (d[(y * fw + x) * 4 + 3] || Math.abs(x - headX) > core + 1) continue;
        let l = x - 1; while (l >= 0 && x - l <= 6 && !d[(y * fw + l) * 4 + 3]) l--;
        let r = x + 1; while (r < fw && r - x <= 6 && !d[(y * fw + r) * 4 + 3]) r++;
        if (l < 0 || r >= fw || x - l > 6 || r - x > 6 || r - l - 1 > 6) continue;
        const o = (y * fw + x) * 4, so = (y * fw + mid) * 4;
        d[o] = d[so]; d[o + 1] = d[so + 1]; d[o + 2] = d[so + 2]; d[o + 3] = 255;
      }
    }
    // the sheet's dark edge on the trunk where the arms came off (before the new arms, so they keep their colour:
    // the engine's INK outline already rings them)
    { const a = new Uint8Array(fw * fh); for (let i = 0; i < fw * fh; i++) a[i] = d[i * 4 + 3] > 0 ? 1 : 0;
      for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) {
        const i = y * fw + x; if (!a[i]) continue;
        const e = !(x > 0 && a[i - 1]) || !(x < fw - 1 && a[i + 1]) || !(y > 0 && a[i - fw]) || !(y < fh - 1 && a[i + fw]);
        if (e && d[i * 4] + d[i * 4 + 1] + d[i * 4 + 2] > 150) { d[i * 4] *= 0.55; d[i * 4 + 1] *= 0.55; d[i * 4 + 2] *= 0.55; }
      } }
    // raise both forearms: shoulder to above the head, 3 px wide, sleeve then skin, a 2 px hand
    const plot = (x: number, y: number, c: [number, number, number]) => { if (x < 0 || y < 0 || x >= fw || y >= fh) return; const o = (y * fw + x) * 4; d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255; };
    for (const sd of [-1, 1]) {
      const x0 = headX + sd * (core - 1), y0 = top + H * 0.26, x1 = headX + sd * (core + 2), y1 = Math.max(1, top - 2);
      const n = Math.ceil(y0 - y1);
      for (let i = 0; i <= n; i++) {
        const t = i / n, x = Math.round(x0 + (x1 - x0) * t), y = Math.round(y0 - (y0 - y1) * t);
        const c = t < 0.3 ? sleeve : sk;
        for (let w = -1; w <= 1; w++) plot(x + w, y, w === sd ? [c[0] * 0.75, c[1] * 0.75, c[2] * 0.75] : w === -sd ? [Math.min(255, c[0] * 1.1), Math.min(255, c[1] * 1.1), Math.min(255, c[2] * 1.1)] : c);   // lit from the upper left
      }
      // a fist at the top of each forearm (R1-021: a bare bar read as a stick): 3 x 3, lit on its left, a thumb, a
      // darker top edge for the knuckles
      const X = Math.round(x1), Y = Math.round(y1), k = (f: number): [number, number, number] => [Math.min(255, sk[0] * f), Math.min(255, sk[1] * f), Math.min(255, sk[2] * f)];
      for (let w = -1; w <= 1; w++) for (let h = 1; h <= 3; h++) plot(X + w, Y - h, k(w === -sd ? 1.15 : 1));
      plot(X + sd * 2, Y - 2, k(0.9));
      for (let w = -1; w <= 1; w++) plot(X + w, Y - 4, k(0.8));
    }
    const cv = document.createElement('canvas'); cv.width = fw; cv.height = fh;
    cv.getContext('2d')!.putImageData(new ImageData(d, fw, fh), 0, 0);
    const t = Texture.from(cv); t.source.scaleMode = 'nearest';
    this.castCache.set(key, t);
    return t;
  }

  /** figure geometry of a frame: top, bottom, head centre, leading-hand point */
  geom(sh: Sheet, fi: number) {
    const key = sh.name + ':' + fi;
    const hit = this.geo.get(key); if (hit) return hit;
    const fw = sh.meta.frameW, fh = sh.meta.frameH;
    const ox = (fi % sh.cols) * fw, oy = Math.floor(fi / sh.cols) * fh;
    const A = (x: number, y: number) => sh.alpha![(oy + y) * sh.W + ox + x] > 0;
    let top = -1, bot = -1;
    for (let y = 0; y < fh && top < 0; y++) for (let x = 0; x < fw; x++) if (A(x, y)) { top = y; break; }
    for (let y = fh - 1; y >= 0 && bot < 0; y--) for (let x = 0; x < fw; x++) if (A(x, y)) { bot = y; break; }
    if (top < 0) return null;
    const H = bot - top;
    let sx = 0, n = 0;
    for (let y = top; y < top + H * 0.12; y++) for (let x = 0; x < fw; x++) if (A(x, y)) { sx += x; n++; }
    const headX = n ? sx / n : fw / 2;
    // the leading hand: the left-most pixel in the arm band
    let hl: [number, number] = [fw * 0.3, top + H * 0.55], hr: [number, number] = [fw * 0.7, top + H * 0.55];
    let minx = fw, maxx = -1;
    for (let y = Math.floor(top + H * 0.3); y < top + H * 0.62; y++) for (let x = 0; x < fw; x++) if (A(x, y)) { if (x < minx) { minx = x; hl = [x, y]; } if (x > maxx) { maxx = x; hr = [x, y]; } }
    const g = { top, bot, headX, handL: hl, handR: hr };
    this.geo.set(key, g);
    return g;
  }

  /** a recoloured layer of one frame: pixels of the figure between rows y0..y1, painted c with a pattern */
  dress(sh: Sheet, fi: number, id: string, c: string, pat: string, c2: string, y0: number, y1: number, vest: boolean): Texture {
    const key = `${sh.name}:${fi}:${id}`;
    const hit = this.dressCache.get(key); if (hit) return hit;
    const fw = sh.meta.frameW, fh = sh.meta.frameH;
    const ox = (fi % sh.cols) * fw, oy = Math.floor(fi / sh.cols) * fh;
    const cv = document.createElement('canvas'); cv.width = fw; cv.height = fh;
    const g = cv.getContext('2d')!; const img = g.createImageData(fw, fh); const d = img.data;
    const col = [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
    const col2 = c2 ? [parseInt(c2.slice(1, 3), 16), parseInt(c2.slice(3, 5), 16), parseInt(c2.slice(5, 7), 16)] : col;
    // horizontal extent of the torso, for vests (the arms stay their own colour)
    let xa = fw, xb = 0;
    for (let y = Math.floor(y0); y < y1; y++) for (let x = 0; x < fw; x++) if (sh.alpha![(oy + y) * sh.W + ox + x] > 0) { xa = Math.min(xa, x); xb = Math.max(xb, x); }
    const wid = xb - xa;
    for (let y = Math.max(0, Math.floor(y0)); y < Math.min(fh, y1); y++) for (let x = 0; x < fw; x++) {
      const si = ((oy + y) * sh.W + ox + x) * 4;
      if (sh.rgba![si + 3] === 0) continue;
      if (vest && (x < xa + wid * 0.22 || x > xb - wid * 0.22)) continue;
      const l = (sh.rgba![si] + sh.rgba![si + 1] + sh.rgba![si + 2]) / 3;
      let k = 0.45 + l / 255 * 0.9;
      let cc = col;
      const ry = Math.floor(y - y0);
      if (pat === 'stripes' && (ry % 7 === 3 || ry % 7 === 4)) cc = col2;
      if (pat === 'quilted' && ry % 4 === 0) k *= 0.75;
      if (pat === 'plates' && ((x - xa) % 5 === 0 || ry % 6 === 0)) k *= 0.6;
      const o = (y * fw + x) * 4;
      d[o] = Math.min(255, cc[0] * k); d[o + 1] = Math.min(255, cc[1] * k); d[o + 2] = Math.min(255, cc[2] * k); d[o + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    const t = Texture.from(cv);
    if (this.dressCache.size > 3000) this.dressCache.clear();
    this.dressCache.set(key, t);
    return t;
  }
}

/** a champion (review R4-012, restyled by STYLE H2/H6.1): marked for the outer danger pixel of its outline (main.ts,
 *  outlineLook) and its body takes a wash of its first affix's colour. No 1.15x scale and no blurred rim any more: a
 *  fractional scale smears the pixel grid, and the extra outline pixel is the "this one is different" cue.
 *  Call after apply() each frame; col null clears it (a champion going down). */
export function champLook(c: Container, col: number | null) {
  (c as any).champ = col !== null;
  if (col === null) return;
  const base = c.children[0] as Sprite;
  // the body takes a wash of the affix colour (review R4-012), unless the red hurt hold is showing
  if (base.tint === 0xffffff) base.tint = ((0xff - ((0xff - (col >> 16 & 255)) * 0.35)) << 16) | ((0xff - ((0xff - (col >> 8 & 255)) * 0.35)) << 8) | (0xff - ((0xff - (col & 255)) * 0.35));
}

const RIMS = new Map<number, ColorMatrixFilter>();   // flat colour, keeping the figure's alpha (a filter runs after tint, so the colour is in the matrix)
export const rimFilter = (col: number) => { let f = RIMS.get(col); if (!f) { f = new ColorMatrixFilter();
  f.matrix = [0, 0, 0, 0, (col >> 16 & 255) / 255, 0, 0, 0, 0, (col >> 8 & 255) / 255, 0, 0, 0, 0, (col & 255) / 255, 0, 0, 0, 1, 0]; RIMS.set(col, f); } return f; };

/** outlines by kind (STYLE H2, review R10-001): one native pixel all round, as offset copies of the figure's own
 *  frame, drawn just behind it. A dark outline (INK for people, dried blood for the turned) is a TINT: a tint multiplies,
 *  so the copies come out near-flat dark with no filter pass, which keeps a street of the turned cheap. A light colour
 *  (your rim, a champion's outer danger pixel) needs a flat-colour filter: those copies sit in one container per figure
 *  with one filter on it. opts.rim: your #F4E6C8 rim on the top-left at alpha 0.35; opts.outer: a second pixel out
 *  (champions, #E2551F). Call after the pose each frame; hide = a corpse or a figure that should carry none. */
export function outlineLook(c: Container, col: number, opts: { rim?: number; outer?: number } = {}, hide = false) {
  const base = c.children[0] as Sprite;
  let O = (c as any).ol as { inner: Sprite[]; rim?: Container; outer?: Container; col: number } | undefined;
  if (!O) {
    O = { inner: [0, 1, 2, 3].map(() => new Sprite()), col };
    (c as any).ol = O; const O2 = O; c.on('destroyed', () => { O2.inner.forEach(s => s.destroy()); O2.rim?.destroy({ children: true }); O2.outer?.destroy({ children: true }); });
  }
  if (opts.rim !== undefined && !O.rim) { O.rim = new Container(); O.rim.addChild(new Sprite()); O.rim.filters = [rimFilter(opts.rim)]; }
  if (opts.outer !== undefined && !O.outer) { O.outer = new Container(); for (let k = 0; k < 8; k++) O.outer.addChild(new Sprite()); O.outer.filters = [rimFilter(opts.outer)]; }
  if (O.outer && opts.outer === undefined) O.outer.visible = false;
  const on = !hide && base.visible && c.visible && c.alpha > 0.05;
  const sx = c.scale.x * base.scale.x, sy = c.scale.y * base.scale.y;
  const put = (s: Sprite, dx: number, dy: number) => {
    s.texture = base.texture; s.anchor.copyFrom(base.anchor); s.rotation = c.rotation; s.scale.set(sx, sy); s.x = c.x + dx; s.y = c.y + dy;
  };
  const parent = c.parent;
  O.inner.forEach((s, k) => {
    if (parent && s.parent !== parent) parent.addChild(s);
    (s as any).actor = true; s.visible = on; if (!on) return;
    const [dx, dy] = [[-1, 0], [1, 0], [0, -1], [0, 1]][k]; put(s, dx, dy);
    s.tint = col; s.alpha = c.alpha; s.zIndex = c.zIndex - 0.02;
  });
  for (const [box, offs, z, alpha] of [[O.rim, [[-1, -1]], c.zIndex - 0.01, 0.35], [O.outer, [[-2, 0], [2, 0], [0, -2], [0, 2], [-1, -1], [1, -1], [-1, 1], [1, 1]], c.zIndex - 0.03, 1]] as [Container | undefined, number[][], number, number][]) {
    if (!box || (box === O.outer && opts.outer === undefined)) continue;
    if (parent && box.parent !== parent) parent.addChild(box);
    (box as any).actor = true; box.visible = on; if (!on) continue;
    box.zIndex = z; box.alpha = alpha * c.alpha;
    box.children.forEach((s, k) => put(s as Sprite, offs[k][0], offs[k][1]));
  }
}
